import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatAppearances, formatPlayerGPG } from '../utils/formatters';

const API_BASE = process.env.REACT_APP_API_URL || '';
const KEY_STORAGE = 'btr_admin_api_key';

const emptyForm = {
  display_name: '',
  profile_photo_url: '',
  year_of_birth: '',
  nationality: '',
  place_of_birth: '',
  height_cm: '',
  weight_kg: '',
  primary_position: '',
  secondary_positions: '',
  preferred_foot: '',
  shirt_number: '',
  bio: '',
  playing_style_tags: '',
  previous_clubs: '',
  youth_clubs: '',
  honours: '',
  highlight_url: '',
  x_url: '',
  instagram_url: '',
  tiktok_url: '',
  youtube_url: '',
  hudl_url: '',
  veo_url: '',
  representative_contact: '',
  contact_email: '',
  contact_visibility: 'private',
  availability_status: '',
};

const inputClasses = 'w-full min-h-[44px] rounded-lg border border-outline-variant/30 bg-surface-container-high px-3 py-2.5 text-sm text-on-surface placeholder:text-on-surface-variant/50 transition-colors duration-150 focus:outline-none focus:border-primary/60 focus-visible:ring-2 focus-visible:ring-primary/70';
const textAreaClasses = 'w-full resize-y rounded-lg border border-outline-variant/30 bg-surface-container-high px-3 py-2.5 text-sm text-on-surface placeholder:text-on-surface-variant/50 transition-colors duration-150 focus:outline-none focus:border-primary/60 focus-visible:ring-2 focus-visible:ring-primary/70';
const labelClasses = 'font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant';

function parseArrayText(value) {
  if (!value) return '';
  if (Array.isArray(value)) return value.join('\n');
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.join('\n');
    } catch {
      return value;
    }
  }
  return String(value);
}

function listFromText(value) {
  return String(value || '')
    .split(/[\n,]/)
    .map(item => item.trim())
    .filter(Boolean);
}

function formFromProfile(profile) {
  const form = { ...emptyForm };
  for (const key of Object.keys(form)) {
    if (['secondary_positions', 'playing_style_tags', 'previous_clubs', 'youth_clubs', 'honours'].includes(key)) {
      form[key] = parseArrayText(profile?.[key]);
    } else if (profile?.[key] !== null && profile?.[key] !== undefined) {
      form[key] = String(profile[key]);
    }
  }
  return form;
}

function TextInput({ label, value, onChange, type = 'text', placeholder = '' }) {
  return (
    <label className="block space-y-1.5">
      <span className={labelClasses}>{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className={inputClasses}
      />
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 4, placeholder = '' }) {
  return (
    <label className="block space-y-1.5">
      <span className={labelClasses}>{label}</span>
      <textarea
        value={value}
        rows={rows}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className={textAreaClasses}
      />
    </label>
  );
}

function SelectInput({ label, value, onChange, children }) {
  return (
    <label className="block space-y-1.5">
      <span className={labelClasses}>{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={inputClasses}
      >
        {children}
      </select>
    </label>
  );
}

export default function AdminPlayerProfiles() {
  const [apiKey, setApiKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || '');
  const [authKey, setAuthKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || '');
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: 'idle', message: '' });

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    'x-api-key': authKey,
  }), [authKey]);

  const searchPlayers = useCallback(async () => {
    if (term.trim().length < 2) {
      setResults([]);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/search?term=${encodeURIComponent(term.trim())}&type=players&limit=12`);
      const json = await response.json().catch(() => null);
      if (!response.ok || !json?.success) throw new Error(json?.error || `HTTP ${response.status}`);
      setResults(json.results || []);
    } catch (err) {
      setStatus({ type: 'error', message: err.message });
    }
  }, [term]);

  useEffect(() => {
    const timer = setTimeout(searchPlayers, 250);
    return () => clearTimeout(timer);
  }, [searchPlayers]);

  async function loadProfile(playerId) {
    if (!authKey) {
      setStatus({ type: 'error', message: 'Enter the admin API key first.' });
      return;
    }
    setLoading(true);
    setStatus({ type: 'idle', message: '' });

    try {
      const response = await fetch(`${API_BASE}/api/admin/player-profile/${playerId}`, { headers });
      const json = await response.json().catch(() => null);
      if (!response.ok || !json?.success) throw new Error(json?.error || `HTTP ${response.status}`);
      setProfile(json.profile);
      setForm(formFromProfile(json.profile));
      sessionStorage.setItem(KEY_STORAGE, authKey);
    } catch (err) {
      setStatus({ type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  }

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function buildPayload() {
    const payload = { ...form };
    for (const field of ['secondary_positions', 'playing_style_tags', 'previous_clubs', 'youth_clubs', 'honours']) {
      payload[field] = listFromText(payload[field]);
    }
    for (const field of ['year_of_birth', 'height_cm', 'weight_kg', 'shirt_number']) {
      payload[field] = payload[field] === '' ? '' : Number(payload[field]);
    }
    return payload;
  }

  async function saveProfile(event) {
    event.preventDefault();
    if (!profile?.player_id) return;
    setSaving(true);
    setStatus({ type: 'idle', message: '' });

    try {
      const response = await fetch(`${API_BASE}/api/admin/player-profile/${profile.player_id}/update`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          updated_by: 'admin-profile-editor',
          payload: buildPayload(),
        }),
      });
      const json = await response.json().catch(() => null);
      if (!response.ok || !json?.success) throw new Error(json?.error || `HTTP ${response.status}`);
      setProfile(json.profile);
      setForm(formFromProfile(json.profile));
      setStatus({ type: 'success', message: `Saved ${json.appliedFields?.length || 0} profile fields.` });
    } catch (err) {
      setStatus({ type: 'error', message: err.message });
    } finally {
      setSaving(false);
    }
  }

  const selectedStats = profile ? {
    total_appearances: profile.total_appearances,
    goals_per_game: profile.goals_per_game,
  } : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-5">
        <div>
          <Link
            to="/admin"
            className="mb-3 inline-flex min-h-[40px] items-center gap-2 font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant transition-colors duration-150 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
          >
            <span className="material-symbols-outlined text-[16px]">arrow_back</span>
            Admin
          </Link>
          <h2 className="font-headline font-expanded text-3xl sm:text-4xl font-bold tracking-tight text-on-surface">Player Profile Editor</h2>
          <p className="text-sm text-on-surface-variant mt-2 max-w-2xl leading-relaxed">Direct edits save as admin-verified profile data. Scraped goals, appearances, club, league, and season remain read-only.</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="password"
            value={apiKey}
            placeholder="Admin API key"
            onChange={(event) => setApiKey(event.target.value)}
            className={`${inputClasses} sm:w-72`}
          />
          <button
            type="button"
            onClick={() => {
              setAuthKey(apiKey);
              sessionStorage.setItem(KEY_STORAGE, apiKey);
            }}
            disabled={!apiKey}
            className="min-h-[44px] rounded-lg bg-primary px-5 text-sm font-bold text-on-primary transition-all duration-150 hover:bg-primary-fixed active:scale-[0.99] disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
          >
            Use Key
          </button>
        </div>
      </div>

      {status.message && (
        <div className={`rounded-xl border px-4 py-3 text-sm font-semibold ${
          status.type === 'success'
            ? 'border-primary/40 bg-primary-container/60 text-on-primary-container'
            : 'border-error/40 bg-error-container/50 text-on-error-container'
        }`}>
          {status.message}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <aside className="xl:col-span-4 space-y-4">
          <div className="rounded-xl border border-outline-variant/20 bg-surface-container p-5 space-y-4">
            <TextInput
              label="Search Player"
              value={term}
              onChange={setTerm}
              placeholder="Lewis Byrne, Kyle Jardine..."
            />
            <div className="space-y-2">
              {results.map((player) => (
                <button
                  key={player.player_id}
                  type="button"
                  onClick={() => loadProfile(player.player_id)}
                  className="w-full min-h-[44px] rounded-lg border border-outline-variant/20 bg-surface-container-high px-3 py-3 text-left transition-colors duration-150 hover:border-primary/30 hover:bg-surface-container-highest active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
                >
                  <p className="text-sm font-semibold text-on-surface">{player.player_name}</p>
                  <p className="mt-0.5 text-xs text-on-surface-variant">{player.club_name} &bull; {player.league_name}</p>
                </button>
              ))}
              {term.trim().length >= 2 && results.length === 0 && (
                <p className="rounded-lg border border-outline-variant/20 bg-surface-container-high px-3 py-3 text-sm text-on-surface-variant">No players found.</p>
              )}
            </div>
          </div>
        </aside>

        <main className="xl:col-span-8">
          {!profile ? (
            <div className="rounded-xl border border-dashed border-outline-variant/40 bg-surface-container-low p-10 text-center">
              <span className="material-symbols-outlined block text-5xl text-on-surface-variant/60 mb-3">manage_accounts</span>
              <p className="text-sm font-semibold text-on-surface">{loading ? 'Loading profile...' : 'Search and select a player to edit.'}</p>
            </div>
          ) : (
            <form onSubmit={saveProfile} className="rounded-xl border border-outline-variant/20 bg-surface-container p-5 sm:p-6 space-y-6">
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                <div>
                  <h3 className="font-headline font-expanded text-2xl font-bold text-on-surface">{profile.player_name}</h3>
                  <p className="mt-0.5 text-[13px] text-on-surface-variant">{profile.club_name} &bull; {profile.league_name}</p>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-on-surface-variant">
                    <span className="rounded-lg border border-outline-variant/20 bg-surface-container-high px-3 py-2 text-primary tabular-nums">{profile.total_goals || 0} goals</span>
                    <span className="rounded-lg border border-outline-variant/20 bg-surface-container-high px-3 py-2 tabular-nums">{formatAppearances(selectedStats)} apps</span>
                    <span className="rounded-lg border border-outline-variant/20 bg-surface-container-high px-3 py-2 tabular-nums">{formatPlayerGPG(selectedStats)} GPG</span>
                    <span className="rounded-lg border border-outline-variant/20 bg-surface-container-high px-3 py-2">{profile.verification_status}</span>
                  </div>
                </div>
                <Link
                  to={`/players/${encodeURIComponent(profile.player_name)}`}
                  className="inline-flex min-h-[40px] items-center gap-2 self-start rounded-lg border border-outline-variant/30 px-3 py-2 text-sm font-semibold text-on-surface-variant transition-colors duration-150 hover:border-primary/40 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
                >
                  Public Profile
                  <span className="material-symbols-outlined text-[18px]">open_in_new</span>
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput label="Display Name" value={form.display_name} onChange={(value) => updateField('display_name', value)} />
                <TextInput label="Position" value={form.primary_position} onChange={(value) => updateField('primary_position', value)} />
                <SelectInput label="Preferred Foot" value={form.preferred_foot} onChange={(value) => updateField('preferred_foot', value)}>
                  <option value="">Unknown</option>
                  <option value="right">Right</option>
                  <option value="left">Left</option>
                  <option value="both">Both</option>
                </SelectInput>
                <TextInput label="Nationality" value={form.nationality} onChange={(value) => updateField('nationality', value)} />
                <TextInput label="Birth Year" type="number" value={form.year_of_birth} onChange={(value) => updateField('year_of_birth', value)} />
                <TextInput label="Place of Birth" value={form.place_of_birth} onChange={(value) => updateField('place_of_birth', value)} />
                <TextInput label="Height CM" type="number" value={form.height_cm} onChange={(value) => updateField('height_cm', value)} />
                <TextInput label="Weight KG" type="number" value={form.weight_kg} onChange={(value) => updateField('weight_kg', value)} />
                <TextInput label="Shirt Number" type="number" value={form.shirt_number} onChange={(value) => updateField('shirt_number', value)} />
                <TextInput label="Availability" value={form.availability_status} onChange={(value) => updateField('availability_status', value)} />
              </div>

              <TextArea label="Bio" value={form.bio} onChange={(value) => updateField('bio', value)} rows={5} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextArea label="Secondary Positions" value={form.secondary_positions} onChange={(value) => updateField('secondary_positions', value)} rows={3} />
                <TextArea label="Playing Style Tags" value={form.playing_style_tags} onChange={(value) => updateField('playing_style_tags', value)} rows={3} />
                <TextArea label="Previous Clubs" value={form.previous_clubs} onChange={(value) => updateField('previous_clubs', value)} rows={4} />
                <TextArea label="Youth Clubs" value={form.youth_clubs} onChange={(value) => updateField('youth_clubs', value)} rows={4} />
                <TextArea label="Honours" value={form.honours} onChange={(value) => updateField('honours', value)} rows={4} />
                <TextArea label="Representative Contact" value={form.representative_contact} onChange={(value) => updateField('representative_contact', value)} rows={4} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput label="Profile Photo URL" value={form.profile_photo_url} onChange={(value) => updateField('profile_photo_url', value)} />
                <TextInput label="Highlight URL" value={form.highlight_url} onChange={(value) => updateField('highlight_url', value)} />
                <TextInput label="X URL" value={form.x_url} onChange={(value) => updateField('x_url', value)} />
                <TextInput label="Instagram URL" value={form.instagram_url} onChange={(value) => updateField('instagram_url', value)} />
                <TextInput label="TikTok URL" value={form.tiktok_url} onChange={(value) => updateField('tiktok_url', value)} />
                <TextInput label="YouTube URL" value={form.youtube_url} onChange={(value) => updateField('youtube_url', value)} />
                <TextInput label="Hudl URL" value={form.hudl_url} onChange={(value) => updateField('hudl_url', value)} />
                <TextInput label="Veo URL" value={form.veo_url} onChange={(value) => updateField('veo_url', value)} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput label="Contact Email" type="email" value={form.contact_email} onChange={(value) => updateField('contact_email', value)} />
                <SelectInput label="Contact Visibility" value={form.contact_visibility} onChange={(value) => updateField('contact_visibility', value)}>
                  <option value="private">Private</option>
                  <option value="public">Public</option>
                </SelectInput>
              </div>

              <div className="flex justify-end border-t border-outline-variant/20 pt-5">
                <button
                  type="submit"
                  disabled={saving}
                  className="min-h-[44px] w-full sm:w-auto rounded-lg bg-primary px-6 text-sm font-bold text-on-primary transition-all duration-150 hover:bg-primary-fixed active:scale-[0.99] disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
                >
                  {saving ? 'Saving...' : 'Save Profile'}
                </button>
              </div>
            </form>
          )}
        </main>
      </div>
    </div>
  );
}
